// ADR 5.9.26p §1/§5 — the sign-in door.
//
// A **component**, not a page, and that is the design decision rather than an
// accident of file placement: 5.9.26t mounts it inline on the shared-plan page
// so a stranger who followed a link can sign in without navigating away from
// what they were reading. That is the 12.8.26b continuity rule — the account is
// the bridge, never a bearer in a URL — so nothing here may depend on being at
// `/signin`.

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { fetchSocialNonce, social, type SocialNonce } from '../api/auth'
import { afterFirstPaint } from '../app/firstPaint'
import { Anchor } from '../ui/Anchor'
import { Button } from '../ui/Button'
import { SectionHeading } from '../ui/SectionHeading'
import { loginCompleted, socialButtonUnavailable, socialLoginFailed } from '../telemetry/auth'
import { signedIn } from '../telemetry/identity'
import { CredentialStatus, EmailPasswordFields, useCredentialSubmit } from './CredentialFields'
import { renderGoogleButton, whenIdentityReady } from './google'
import { useAuthSession } from './session'
import { OfflineHint } from '../offline/OfflineHint'
import { useOnlineStatus } from '../offline/onlineSignal'
import { PageFrame } from '../layout/frame/PageFrame'
import { withNext } from '../app/routeHelpers'

// ADR 24.9.26af §3 — reserves the box GIS's `size: 'large'` button arrives in
// so its late arrival does not push the email form down. ADR 2.10.26a §2: the
// height is the iframe GIS mounts around the 40 px button, not the button —
// 44 px, and the larger of two browsers if they differ, since an empty box a
// few pixels tall shifts nothing and a short one does. ADR 3.10.26e §2: a fixed
// `h-11`, not a `min-h-11` floor — GIS's own build sequence changes the host's
// height while it renders, and a fixed box keeps the form below in place
// whatever order that happens in (overflow paints over, it does not shift). No
// `display` utility here: one would override `[hidden]` and keep the box after
// a failure.
const GOOGLE_HOST = 'h-11'

type NonceArrival = { issued: SocialNonce | null; at: number }

/**
 * ADR 2.10.26a §3 — the round's nonce, fetched behind the same first-paint
 * gate the script waits on (1.10.26p §3), so the two travel together instead
 * of one after the other. Never from the mount effect directly: a passive
 * effect can run before the paint, which would put a preflight and a POST
 * ahead of the observed LCP. A run torn down before the paint (Strict mode's
 * first mount) never fetches; its promise simply never resolves.
 *
 * The trade, taken for one round trip saved before the button is usable: a
 * script that never loads (an ad-blocker, a strict network) costs one unused
 * nonce, which is single-use and expires on its own. The degrade to the email
 * form is unchanged and the nonce is dropped.
 */
function issueNonceAfterPaint(isCancelled: () => boolean): Promise<NonceArrival> {
  return new Promise((resolve) =>
    afterFirstPaint(window, () => {
      if (isCancelled()) return
      void fetchSocialNonce().then((issued) => resolve({ issued, at: Date.now() }))
    }),
  )
}

/**
 * The TTL clock started when the nonce arrived, possibly a whole script load
 * before the button rendered, so the round ends with the server's, not later.
 */
function ttlLeftMs(issued: SocialNonce, arrivedAt: number): number {
  return Math.max(0, issued.ttl_seconds * 1000 - (Date.now() - arrivedAt))
}

/**
 * The GIS button, or nothing when the script never loads (§1).
 *
 * Rendering into a ref rather than JSX because the button is Google's own
 * iframe: `renderButton` owns the subtree, so React must not try to.
 *
 * **ADR 15.9.26c §3 — the render waits for the script.** Since 24.9.26af the
 * script is inserted on demand by `whenIdentityReady()`, so it is never there
 * at mount on a cold cache; a one-shot probe would mark the button `failed`
 * and never ask again. `whenIdentityReady()` resolves on the script's `load` (or at once when
 * the global is already there), bounded by a timeout so a blocked script still
 * degrades to the email form, now with a counted reason. The `cancelled` flag
 * is what makes an unmount mid-wait (and Strict mode's double effect) safe: a
 * late resolve on a dead component is dropped rather than calling
 * `renderButton` into a detached node — and because the check precedes the
 * emit, an orphaned wait's timeout counts nothing.
 *
 * **ADR 6.9.26j gives the `social()` promise its first rejection handler**, so
 * this row is a small behaviour change as well as telemetry: an unhandled
 * rejection becomes a handled one. `social_login_failed` is the only signal
 * anyone will ever get for a Google outage or a `client_id` that stopped
 * matching `aud` — the user sees a button that does nothing and reports
 * nothing.
 */
function GoogleSignInButton() {
  const { adopt } = useAuthSession()
  const host = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)
  // ADR 24.9.26v §4 — one round is one nonce and one GIS `initialize`. The
  // nonce is single-use server-side (a 409 spends it too), so every settled
  // attempt, and the nonce's TTL lapsing, starts the next round: a fresh
  // fetch, a re-initialise, and — through `key` — a fresh host for the button.
  const [round, setRound] = useState(0)

  useEffect(() => {
    let cancelled = false
    let ttlTimer: ReturnType<typeof setTimeout> | undefined
    const nextRound = () => {
      if (!cancelled) setRound((r) => r + 1)
    }
    const issuing = issueNonceAfterPaint(() => cancelled)
    void whenIdentityReady().then(async (ready) => {
      if (cancelled || host.current === null) return
      if (ready.api === null) {
        socialButtonUnavailable(ready.reason)
        setFailed(true)
        return
      }
      const { issued, at } = await issuing
      if (cancelled || host.current === null) return
      const nonce = issued?.nonce ?? null
      // The `false` branch survives for the one case readiness cannot see: an
      // empty `GOOGLE_CLIENT_ID` is a build configuration, not a script
      // outcome, so it hides the `div` without an event.
      if (!renderGoogleButton(host.current, credentialHandler(adopt, nonce, nextRound), nonce)) {
        setFailed(true)
      } else if (issued !== null) {
        ttlTimer = setTimeout(nextRound, ttlLeftMs(issued, at))
      }
    })
    return () => {
      cancelled = true
      clearTimeout(ttlTimer)
    }
  }, [adopt, round])

  return (
    <div key={round} ref={host} hidden={failed} className={GOOGLE_HOST} data-testid="google-signin" />
  )
}

/**
 * The GIS callback for one round: post the credential with that round's nonce,
 * then — whatever the outcome — `settled`, which starts the next round.
 */
function credentialHandler(
  adopt: ReturnType<typeof useAuthSession>['adopt'],
  nonce: string | null,
  settled: () => void,
) {
  return (idToken: string) => {
    void social(idToken, nonce)
      .then(async (response) => {
        await adopt(response)
        // A login, never a registration: the merge belongs to the one moment
        // the account was created, and `/auth/social` answers the same way
        // whether it just created the account or found it (§1b).
        loginCompleted('google')
        signedIn(response.user_id, false)
      })
      .catch((cause: unknown) => {
        socialLoginFailed(cause instanceof Error ? cause.message : String(cause))
      })
      .finally(settled)
  }
}

export function SignInPanel() {
  const { t } = useTranslation()
  const form = useCredentialSubmit('login')
  // ADR 23.9.26v §6 — a new sign-in needs the network. Google's button is its
  // own iframe, which cannot load offline anyway, and is left alone.
  const online = useOnlineStatus()
  const [params] = useSearchParams()

  return (
    <PageFrame width="form" as="section" className="space-y-4">
      <SectionHeading>{t('auth_sign_in')}</SectionHeading>
      <GoogleSignInButton />
      <form className="space-y-3" onSubmit={form.submit}>
        <EmailPasswordFields form={form} />
        <Button type="submit" variant="primary" className="w-full" disabled={form.busy || !online}>
          {t('auth_sign_in')}
        </Button>
        <OfflineHint />
        <CredentialStatus form={form} />
      </form>
      {/* Backend ADR 25.9.26n §5 — keeps `next`, so a first-time user registers back to where they were going. */}
      <Anchor className="block" to={withNext('/register', params.get('next'))}>
        {t('auth_no_account')}
      </Anchor>
    </PageFrame>
  )
}
